import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {
    isOrganisationActive,
    OrganisationAdminListItem,
    OrganisationDetail
} from '@shared/api/models/query/organisation.model';
import {AdminUserLookupItem} from '@shared/api/models/query/contract.model';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkedRecordsTabBase} from '@shared/components/linked-records/linked-records-tab.base';
import {LinkRecordsModalConfig} from '@shared/components/linked-records/link-records.modal';

/**
 * UNIVAPLAT-3698: Organisation admins tab of the organisation detail page. The page only renders
 * it for users holding MANAGE_ORGANISATION_ADMINS (general admin); the API enforces the same.
 *
 * - Lists the admins linked to the organisation, newest first (names and emails are staff data,
 *   shown as plain text). Link and unlink need an active organisation.
 * - The API answers 422 on the admin list of a deactivated or deleted organisation, so for those
 *   the tab shows a message and does not load.
 * - The link popup lists the users with the Admin Org role not yet linked, matched by email and
 *   falling back to name (list items carry the link id, not the user id), and adds the selection
 *   with one `addAdmins` call. Unlink uses the `organisation_admins` record id.
 * - Backend owns the error text. Admin names and emails are never logged and are dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-admins-tab',
    standalone: true,
    imports: [DatePipe, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './organisation-admins-tab.component.html',
    styleUrl: './organisation-admins-tab.component.scss'
})
export class OrganisationAdminsTabComponent extends LinkedRecordsTabBase<OrganisationAdminListItem> implements OnChanges {
    @Input() organisation!: OrganisationDetail;

    protected readonly entityLabel = 'Organisation admin';

    private readonly organisationsService = inject(OrganisationsService);
    private readonly authService = inject(AuthService);

    get isActive(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    get canWrite(): boolean {
        return this.isActive && this.hasPermission(Permissions.MANAGE_ORGANISATION_ADMINS);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['organisation']) {
            return;
        }
        const previous = changes['organisation'].previousValue as OrganisationDetail | undefined;
        const current = this.organisation;
        // Reload on first render, a different organisation, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isOrganisationActive(previous) !== isOrganisationActive(current)) {
            void this.load();
        }
    }

    rowId(row: OrganisationAdminListItem): number {
        return row.id;
    }

    adminName(admin: Pick<OrganisationAdminListItem, 'firstName' | 'lastName'>): string {
        return `${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim();
    }

    protected async fetch(): Promise<OrganisationAdminListItem[]> {
        if (!this.organisation || !this.isActive) {
            return [];
        }
        const rows = await this.organisationsService.listAdmins(this.organisation.id);
        // Newest first; a missing date sorts last.
        return [...rows].sort((a, b) => Date.parse(b.addedOn ?? '') - Date.parse(a.addedOn ?? '') || 0);
    }

    protected async unlinkOne(row: OrganisationAdminListItem): Promise<void> {
        await this.organisationsService.removeAdmin(this.organisation.id, row.id);
    }

    protected linkModalConfig(): LinkRecordsModalConfig<AdminUserLookupItem> {
        const organisationId = this.organisation.id;
        // Snapshot of who is linked now; list items carry the link id, so match by email, then name.
        const linked = new Set(this.rows.map(admin => this.identity(admin)));
        return {
            heading: 'Link organisation admins',
            titleId: 'link-organisation-admins-title',
            columns: [
                {header: 'First name', value: user => user.firstName},
                {header: 'Last name', value: user => user.lastName},
                {header: 'Email', value: user => user.email},
            ],
            loader: async () => {
                const all = await this.organisationsService.listCandidateAdmins();
                return all.filter(user => !linked.has(this.identity(user)));
            },
            submitLabel: 'Add selected to organisation',
            successMessage: 'Organisation admins linked',
            emptyMessage: 'No organisation admins left to link',
            submit: async (ids: number[]) => {
                await this.organisationsService.addAdmins(organisationId, ids);
            },
        };
    }

    private identity(user: {email: string | null, firstName: string | null, lastName: string}): string {
        const email = (user.email ?? '').trim().toLowerCase();
        return email !== ''
            ? `e:${email}`
            : `n:${(user.firstName ?? '').trim().toLowerCase()} ${(user.lastName ?? '').trim().toLowerCase()}`;
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }
}
